import {cookies, Notifier, router, Window} from "@intermesh/goui";
import {HttpVerb, ServerApi} from "../util/ServerApi.js";
import {CurrentItem} from "./CurrentItem.js";
import {ParticipantEntity, ParticipantLicense} from "../data/ParticipantsStore.js";
import {t} from "../util/Translate.js";
import {CurrentCourse} from "./CurrentCourse.js";
import {CurrentSection} from "./CurrentSection.js";
import {CourseEntity} from "../data/CoursesStore.js";
import {IfwFormat} from "../util/IfwFormat.js";
import {frontendParticipantDS} from "../DataSources";

export class CurrentParticipant extends CurrentItem {
	static storageItem = "currentParticipant";
	static baseRoute = "elearning/frontend/participants";
	static returnProperties = "*,customFields,attempts,user[*],licenses[*,course[*,blocks[block]]]," +
		"sections[id,name,licenses[*,course]],missingDocumentTypes[*,documentType[*,id,name]]," +
		"documents[id,documentTypeId,blobId,identification,documentDate,expiryDate,documentBlob[name,contentType]," +
		"documentType[name]],missingDocumentTypes[documentTypeId],tokenLicense";

	static async readIf() {
		if (!sessionStorage.getItem(CurrentParticipant.storageItem)) {
			if (cookies.get("accessToken") || sessionStorage.getItem("invitationToken")) {
				let p = {returnProperties: CurrentParticipant.returnProperties};
				if (CurrentParticipant.isInvited()) {
					Object.assign(p, {"invitationToken": sessionStorage.getItem("invitationToken")});
				}
				const url = ServerApi.url(CurrentParticipant.baseRoute + "/current", p);
				try {
					const response = await ServerApi.call(url, {}, "GET");
					if (response.data) {
						sessionStorage.setItem(CurrentParticipant.storageItem, JSON.stringify(response.data));
					} else if (response.message) {
						Notifier.warning(t(response.message));
						if (sessionStorage.getItem("invitationToken")) {
							sessionStorage.removeItem("invitationToken");
							await CurrentParticipant.readIf();
						}
					}
				} catch (err) {
					Notifier.error(err);
				}
			}
		}
		return CurrentParticipant.get();
	}

	static get(): ParticipantEntity {
		let cp = JSON.parse(sessionStorage.getItem(CurrentParticipant.storageItem)!) || {};
		if (cp.lastName && !cp.attempts) {
			cp.attempts = [];
		}
		if (!cp.languageId) {
			cp.languageId = sessionStorage.getItem("languageId") || 1; // There's always Dutch
		}
		return cp;
	}

	static async refresh(response: { success: boolean, data: any, message?: string }) {
		if (!response.success) {
			Notifier.error("API error: " + (response.message || "Unknown error"));
			await Promise.reject(response.message || "Unknown error")
		}
		if (response.data.id) {
			// Force a refresh from the API
			const participantData = await frontendParticipantDS.single(response.data.id,);
			if(participantData) {
				sessionStorage.setItem(CurrentParticipant.storageItem, JSON.stringify(participantData));
			}
		} else {
			sessionStorage.setItem(CurrentParticipant.storageItem, JSON.stringify(response.data));
		}
	}

	static save(body = {}): Promise<any> {
		const cp = CurrentParticipant.get();
		let route = "elearning/frontend/participants/", method = "POST" as HttpVerb;
		if (cp.id) {
			route += cp.id;
			method = "PUT";
		}
		const url = ServerApi.url(route, {
			returnProperties: "*,customFields,attempts,user"
		});
		if (typeof body !== "undefined") {
			return ServerApi.call(url, body, method);
		}

		return ServerApi.call(url, cp, method);
	}

	/**
	 * Check if there is a known invitation token
	 */
	static isInvited(): boolean {
		return !!sessionStorage.getItem("invitationToken");
	}

	/**
	 * Check whether participant has been redirected to course as per invitation.
	 */
	static isRedirectedToInvitedCourse(): boolean {
		if (!localStorage.getItem("isRedirectedToInvitedCourse")) {
			return false;
		}
		return parseInt(localStorage.getItem("isRedirectedToInvitedCourse")!) === 1;
	}

	static getCourseDuration(course: CourseEntity): string {
		let duration = 0, b, d;
		for (let i = 0, l = course.blocks.length; i < l; i++) {
			b = course.blocks[i].block;
			if (!CurrentParticipant.hasCompletedBlock(b.id)) {

				if (b.estimatedDuration == null) {
					return "PT0M";
				}
				duration += IfwFormat.ifwDurationToMinutes(b.estimatedDuration);
			}
		}

		return IfwFormat.ifwMinutesToDuration(duration);
	}

	static hasCompletedBlock(id: string, lastAttemptIsLeading = false): boolean {
		let hasCompleted = false;
		const cp = CurrentParticipant.get();
		if (!cp.attempts) {
			return false;
		}
		const attempts = cp.attempts!.filter((attempt) => (attempt.blockId == id));
		for (let attempt of attempts) {
			hasCompleted = (attempt.succeeded && new Date(attempt.expiresAt) > new Date());
			if (hasCompleted && !lastAttemptIsLeading) {
				break;
			}
		}

		return hasCompleted;
	}


	/**
	 * Get course and section Ids from invitation, set session variables and redirect to course
	 */
	static async goToInvitedCourse() {
		if (!CurrentParticipant.isInvited()) {
			return;
		}

		const cp = CurrentParticipant.get(), tokenLicense = cp.tokenLicense as unknown as ParticipantLicense;
		if (!tokenLicense) {
			// sessionStorage.removeItem("invitationToken");
			await CurrentParticipant.readIf(); // Refresh participant without token
			await Window.alert(t('EXCEPTION_INVITATION_NO_LICENSES_LEFT'), t('Invitation fully used'));
		} else {
			await CurrentCourse.load(tokenLicense!.courseId);
			await CurrentSection.load(tokenLicense!.sectionId);

			// #975 remove invitation token as soon as the course and section are known
			sessionStorage.removeItem("invitationToken");

			// If this parameter is set, an email is sent upon successful completion.
			sessionStorage.setItem("isRedirectedToInvitedCourse", "1");
			void router.goto("course-status")
		}

	}
}


export const currentParticipant = () => Object.assign(new CurrentParticipant());
